import type { ReactNode } from "react";

/**
 * Un campo con su etiqueta, su pista y su error DEBAJO, no en un resumen: los
 * 400 con `errors` se pintan junto a cada campo por su nombre camelCase
 * (SPEC 15.3).
 */
export function Field(props: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="f">
      <span>{props.label}</span>
      {props.children}
      {props.hint !== undefined && <span className="hint">{props.hint}</span>}
      {props.error !== undefined && <span className="err">{props.error}</span>}
    </label>
  );
}

/**
 * SPEC 15.5: `type="text"` con `inputMode="decimal"`, NUNCA `type="number"`. En
 * movil, number oculta el punto en varios teclados y acepta la letra `e`.
 */
export function NumberField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  placeholder?: string;
}): ReactNode {
  return (
    <Field label={props.label} error={props.error} hint={props.hint}>
      <input
        className="ctl num"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </Field>
  );
}

export function Select(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  error?: string | undefined;
  hint?: string | undefined;
  placeholder?: string;
}): ReactNode {
  return (
    <Field label={props.label} error={props.error} hint={props.hint}>
      <select
        className="ctl"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        <option value="">{props.placeholder ?? "Sin elegir"}</option>
        {props.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function TextArea(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  placeholder?: string;
}): ReactNode {
  return (
    <Field label={props.label} error={props.error} hint={props.hint}>
      <textarea
        className="ctl"
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </Field>
  );
}

/** `datetime-local`: el navegador ya pone el selector nativo, que en movil gana. */
export function MomentField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
}): ReactNode {
  return (
    <Field label={props.label} error={props.error} hint={props.hint}>
      <input
        className="ctl num"
        type="datetime-local"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </Field>
  );
}

/**
 * Dos o tres opciones que caben a la vista: un desplegable las escondería, y
 * elegir compra o venta es la decision que mas se toca de toda la app.
 */
export function Segmented<T extends string>(props: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  error?: string | undefined;
  hint?: string | undefined;
}): ReactNode {
  return (
    <div className="f">
      <span>{props.label}</span>
      <div className="seg" role="group" aria-label={props.label}>
        {props.options.map((option) => (
          <button
            key={option.value}
            type="button"
            data-v={option.value}
            className={option.value === props.value ? "active" : ""}
            aria-pressed={option.value === props.value}
            onClick={() => props.onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {props.hint !== undefined && <span className="hint">{props.hint}</span>}
      {props.error !== undefined && <span className="err">{props.error}</span>}
    </div>
  );
}
